import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "react-query";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Button,
  CircularProgress,
  Divider as MuiDivider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@material-ui/core";
import LockIcon from "@material-ui/icons/Lock";
import RefreshIcon from "@material-ui/icons/Refresh";

import styled from "styled-components/macro";
import { spacing } from "@material-ui/system";
import { Helmet } from "react-helmet-async";
import { Alert, Skeleton } from "@material-ui/lab";
import MaterialTable from "material-table";
import axios from "axios";
import { useAuth0 } from "@auth0/auth0-react";

import useFetchData from "../../hooks/useFetchData";
import Loader from "../../components/Loader";
import { formatter } from "../../utils";
import DataAdminTableBulkUpdateMeterReadings from "../../components/DataAdminTableBulkUpdateMeterReadings";
import { useApp } from "../../AppProvider";

const Divider = styled(MuiDivider)(spacing);
const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const TableWrapper = styled.div`
  overflow-y: auto;
  max-width: calc(100vw - ${(props) => props.theme.spacing(12)}px);
  max-height: calc(100% - 48px);
`;

// Fiscal year runs Sep–Aug, so Sep onward counts toward the next fiscal year.
function getFiscalYear(date) {
  const year = date.getFullYear();
  return date.getMonth() >= 8 ? year + 1 : year;
}

function getDefaultYear() {
  return getFiscalYear(new Date());
}

// 0 is the "All" entry in the month selector, not a real month number.
const ALL_MONTHS = 0;

// The page opens on the month that just closed — that's the reading owners come
// here to enter. Building the date with a month index of -1 rolls January back
// to December of the prior calendar year on its own, so that case needs no
// special handling. The fiscal year comes from that previous month rather than
// from today, and the two only differ in September: its predecessor, August, is
// the last month of the *prior* fiscal year. December -> January falls inside a
// fiscal year, so it does not shift the year.
function getDefaultPeriod() {
  const today = new Date();
  const previousMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  return {
    year: getFiscalYear(previousMonth),
    month: previousMonth.getMonth() + 1,
  };
}

// Settle that default against the fiscal years this user actually has. When the
// previous month's fiscal year isn't one of them — a September for an owner with
// no prior-year assignment — don't shift the year. Stay on a year they do have
// and fall back to "All", since the previous month number would otherwise point
// at a month of the wrong fiscal year (August of the year that just started is
// still ~12 months out).
function resolveDefaultPeriod(years) {
  const preferred = getDefaultPeriod();
  if (years.includes(preferred.year)) return preferred;

  const currentFiscalYear = getDefaultYear();
  return {
    year: years.includes(currentFiscalYear) ? currentFiscalYear : years[0],
    month: ALL_MONTHS,
  };
}

// Shared empty default for react-query results, so a query that hasn't resolved
// yet doesn't hand a brand-new [] to downstream memos on every single render.
const EMPTY_ROWS = [];

// A reading is editable only until it has been entered. Once meter_reading is
// set, the row is locked — owners contact the District to change a saved
// reading (the backend enforces the same rule).
const isUnentered = (rowData) =>
  rowData.meter_reading === null || rowData.meter_reading === undefined;

// Conditional formatting for the monthly detail table, taken from the client's
// Excel mockup. Keyed on drought_stage_ndx rather than the stage name, so
// renaming a stage in list_drought_stages can't silently drop the colors.
const DROUGHT_STAGE_STYLES = {
  0: { backgroundColor: "#00B050", color: "#000000" }, // No Drought/Conservation
  1: { backgroundColor: "#FFFF00", color: "#000000" }, // Alarm/Reduction
  2: { backgroundColor: "#ED7D31", color: "#000000" }, // Critical
  3: { backgroundColor: "#FF0000", color: "#000000" }, // Exceptional
  4: { backgroundColor: "#000000", color: "#FF0000" }, // Emergency Response
};

// Overage escalation, applied to both "% of Target" and "Overage Level" (the
// mockup fills both cells the same). Only A and B appear in the mockup; C is
// orange per the client's note. Blank/none stays unfilled.
const OVERAGE_LEVEL_STYLES = {
  A: { backgroundColor: "#E2EFDA" },
  B: { backgroundColor: "#FFFF00" },
  C: { backgroundColor: "#FFC000" },
};

// Percentages come off the view as fractions (0.887) and display as "88.7%".
const percentFormatter = (value) =>
  value === null || value === undefined || isNaN(value)
    ? ""
    : `${(Number(value) * 100).toFixed(1)}%`;

// The monthly detail table is a plain MUI table rather than a MaterialTable,
// following MeterReadingsHistory.js. A read-only table of 12 fixed rows needs
// nothing MaterialTable provides, and staying off it avoids the column-mutation
// trap described above `summaryColumns` below.
//
// These two helpers are pure and are never handed to a table, so they are safe
// to keep at module scope. The column array is NOT — see the component.
const renderMonthlyCell = (row, colId) => {
  switch (colId) {
    case "pct_base_of_annual":
      return percentFormatter(row.pct_base_of_annual);
    case "pct_of_target":
      return percentFormatter(row.pct_of_target);
    case "baseline":
    case "drought_target":
    case "pumpage":
      return formatter(row[colId], 0);
    default:
      return row[colId] ?? "";
  }
};

const getMonthlyCellStyle = (row, colId) => {
  if (colId === "drought_stage") {
    return DROUGHT_STAGE_STYLES[row.drought_stage_ndx];
  }
  if (colId === "pct_of_target" || colId === "overage_level") {
    return OVERAGE_LEVEL_STYLES[row.overage_level];
  }
  return undefined;
};

const MonthlyHeaderCell = styled(TableCell)`
  font-weight: 600;
  font-size: 0.75rem;
  white-space: nowrap;
  background-color: ${(props) => props.theme.palette.background.default};
  padding: ${(props) => props.theme.spacing(1)}px;
`;

const MonthlyCell = styled(TableCell)`
  font-size: 0.75rem;
  white-space: nowrap;
  padding: ${(props) => props.theme.spacing(1)}px;
  border: 1px solid #eee;
`;

const MonthlyFooterCell = styled(MonthlyCell)`
  font-weight: 700;
  color: inherit;
  border-top: 2px solid #666;
  background-color: #f5f5f5;
`;

const MyMeterReadings = () => {
  const { getAccessTokenSilently } = useAuth0();
  const { doToast } = useApp();

  // Seeded from the previous month so the first fetch is already the right
  // period; reconciled against the user's actual fiscal years once those load.
  const [selectedYear, setSelectedYear] = useState(
    () => getDefaultPeriod().year
  );
  const [selectedMonth, setSelectedMonth] = useState(
    () => getDefaultPeriod().month
  );
  const [selectedGrouping, setSelectedGrouping] = useState(null);

  const [monthsLookup] = useFetchData("list-months", [], true);
  const monthsLookupForTable = useMemo(() => {
    const converted = {};
    (monthsLookup || []).forEach((item) => {
      converted[item.month_number] = item.month_name;
    });
    return converted;
  }, [monthsLookup]);

  // Adjustment reasons are District-set; owners only read them (see below).
  const [adjustmentReasonLookup] = useFetchData(
    "ui-list-meter-reading-adjustment-reason",
    [],
    true
  );
  const adjustmentReasonLookupForTable = useMemo(() => {
    const converted = {};
    (adjustmentReasonLookup || []).forEach((item) => {
      converted[item.mr_adjustment_reason_ndx] = item.mr_adjustment_reason_desc;
    });
    return converted;
  }, [adjustmentReasonLookup]);

  // Reporting status (On Time / Late / ...) is District-set; owners read it so
  // they can see how their past months stood, but cannot change it.
  const [reportStatusLookup] = useFetchData(
    "ui-list-meter-reading-report-status",
    [],
    true
  );
  const reportStatusLookupForTable = useMemo(() => {
    const converted = {};
    (reportStatusLookup || []).forEach((item) => {
      converted[item.mr_report_status_ndx] = item.mr_report_status_desc;
    });
    return converted;
  }, [reportStatusLookup]);

  // The groupings (Permit Owner/Group) this login is assigned, for the year.
  const { data: groupings = [] } = useQuery(
    ["my-meter-readings-groupings", selectedYear],
    async () => {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/my-meter-readings/groupings/${selectedYear}`,
        { headers }
      );
      return data;
    },
    // Keep the prior year's groupings while refetching so the selected grouping
    // isn't briefly cleared on a year change (which would blank the page with no
    // loader). A user's grouping_ndx is stable across years, so it stays valid.
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  // Auto-select when there is exactly one grouping; otherwise keep the user's
  // choice if it is still valid for the selected year.
  useEffect(() => {
    if (!groupings.length) {
      setSelectedGrouping(null);
      return;
    }
    const stillValid = groupings.some(
      (g) => g.grouping_ndx === selectedGrouping
    );
    if (groupings.length === 1) {
      setSelectedGrouping(groupings[0].grouping_ndx);
    } else if (!stillValid) {
      setSelectedGrouping(null);
    }
  }, [groupings]); // eslint-disable-line react-hooks/exhaustive-deps

  // All readings the user is allowed to see for the year/month. Already scoped
  // to the caller server-side; we filter to the selected grouping client-side.
  const {
    data: allReadings = EMPTY_ROWS,
    isFetching: readingsFetching,
    refetch,
  } = useQuery(
    ["my-meter-readings", selectedYear, selectedMonth],
    async () => {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/my-meter-readings/${selectedYear}/${selectedMonth}`,
        { headers }
      );
      return data;
    },
    // Keep the prior rows visible (under the table's loading overlay) while the
    // new year/month loads, rather than flashing an empty table.
    { keepPreviousData: true, refetchOnWindowFocus: false }
  );

  const readings = useMemo(() => {
    if (!selectedGrouping) return [];
    // material-table mutates the row objects it is given (it stamps a
    // `tableData` field on each). Hand it shallow copies so it never mutates
    // the react-query cache, which otherwise corrupts state and crashes the
    // table on repeated year/month changes.
    return (allReadings || [])
      .filter((row) => (row.assoc_permits || []).includes(selectedGrouping))
      .map((row) => ({ ...row }));
  }, [allReadings, selectedGrouping]);

  // Allocation/usage summary for the user's groupings. The view behind this is
  // slow (~4s) and a year filter doesn't help, so fetch it once for all years
  // and filter by year/grouping client-side — keeps year changes instant.
  const {
    data: summaryRows = EMPTY_ROWS,
    isLoading: summaryLoading,
    refetch: refetchSummary,
  } = useQuery(
    ["my-meter-readings-summary"],
    async () => {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/my-meter-readings/summary`,
        { headers }
      );
      return data;
    },
    { refetchOnWindowFocus: false }
  );

  // Shallow copies: MaterialTable stamps a `tableData` field onto every row it
  // is handed, so a bare .filter() of the react-query cache would let it mutate
  // the cached objects.
  const currentSummary = useMemo(
    () =>
      summaryRows
        .filter(
          (row) =>
            row.grouping_ndx === selectedGrouping &&
            row.fiscal_year === selectedYear
        )
        .map((row) => ({ ...row })),
    [summaryRows, selectedGrouping, selectedYear]
  );

  // Month-by-month detail for the selected grouping + fiscal year. Unlike the
  // annual summary above, this one is built from
  // ui_report_drought_compliance_pre_table, so it only reflects new readings
  // once that table is rebuilt — which is what Recalculate Summary does.
  const {
    data: monthlySummary = EMPTY_ROWS,
    isFetching: monthlyFetching,
    refetch: refetchMonthlySummary,
  } = useQuery(
    ["my-meter-readings-monthly-summary", selectedYear, selectedGrouping],
    async () => {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/my-meter-readings/monthly-summary/${selectedYear}/${selectedGrouping}`,
        { headers }
      );
      return data;
    },
    { enabled: !!selectedGrouping, refetchOnWindowFocus: false }
  );

  // Totals row, computed here rather than in the database — it is a plain
  // roll-up of what is already on screen, so a data source for it would buy
  // nothing. Rendered as a real <TableFooter>, so it is never part of the row
  // data and can't be sorted or paged away from the bottom.
  const monthlyTotals = useMemo(() => {
    if (!monthlySummary.length) return null;

    const sum = (field) =>
      monthlySummary.reduce((acc, row) => acc + (Number(row[field]) || 0), 0);
    const droughtTarget = sum("drought_target");
    const pumpage = sum("pumpage");

    return {
      mo: "Total",
      pct_base_of_annual: sum("pct_base_of_annual"),
      baseline: sum("baseline"),
      drought_stage: "",
      drought_target: droughtTarget,
      pumpage,
      // Ratio of the totals, not an average of the monthly ratios — matches the
      // mockup (180,797,000 / 189,812,720 = 95.3%).
      pct_of_target: droughtTarget ? pumpage / droughtTarget : null,
      overage_level: "",
    };
  }, [monthlySummary]);

  // Recalculate the summary for the selected grouping + fiscal year. Rebuilds
  // the drought compliance slice server-side (so the readings just entered are
  // picked up without waiting for the nightly job), then refetches the summary
  // and the readings so the page shows the new numbers.
  const [isRecalculating, setIsRecalculating] = useState(false);
  const handleRecalculate = async () => {
    if (!selectedGrouping || isRecalculating) return;
    setIsRecalculating(true);
    try {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(
        `${process.env.REACT_APP_ENDPOINT}/api/my-meter-readings/recalculate`,
        { fiscal_year: selectedYear, grouping_ndx: selectedGrouping },
        { headers }
      );
      await Promise.all([refetchSummary(), refetchMonthlySummary(), refetch()]);
      doToast("success", "Summary was recalculated with your latest readings");
    } catch (err) {
      console.error(err);
      doToast("error", err?.message ?? "Something went wrong");
    } finally {
      setIsRecalculating(false);
    }
  };

  // Fiscal years this user actually has groupings for -> drives the year
  // selector, so a year with nothing to enter can never be chosen.
  const { data: years = [], isFetching: yearsFetching } = useQuery(
    ["my-meter-readings-years"],
    async () => {
      const token = await getAccessTokenSilently();
      const headers = { Authorization: `Bearer ${token}` };
      const { data } = await axios.get(
        `${process.env.REACT_APP_ENDPOINT}/api/my-meter-readings/years`,
        { headers }
      );
      return data;
    },
    { refetchOnWindowFocus: false }
  );

  // Apply the year/month default once the user's fiscal years are known, then
  // leave the selectors alone — this must not stomp on a choice the user has
  // made since. Nothing can be chosen before it runs anyway: the selectors are
  // behind the `yearsFetching` loader below.
  const defaultPeriodResolved = useRef(false);
  useEffect(() => {
    if (!years.length) return;

    if (!defaultPeriodResolved.current) {
      defaultPeriodResolved.current = true;
      const { year, month } = resolveDefaultPeriod(years);
      setSelectedYear(year);
      setSelectedMonth(month);
      return;
    }

    // Safety net for a later refetch that drops the selected year: prefer the
    // current fiscal year when the user has it, otherwise their most recent.
    if (!years.includes(selectedYear)) {
      setSelectedYear(
        years.includes(getDefaultYear()) ? getDefaultYear() : years[0]
      );
    }
  }, [years]); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = useMemo(
    () => {
      // Build filter dropdowns from the values actually present in the table, so
      // the Meter ID / Reporting Status filters only offer real options (not the
      // full lookup of meters/statuses that may never appear for this grouping).
      const meterIdLookup = {};
      const reportStatusInData = {};
      const reasonInData = {};
      readings.forEach((row) => {
        if (row.meter_id != null) meterIdLookup[row.meter_id] = row.meter_id;
        const status = row.mr_report_status_ndx;
        if (status != null) {
          reportStatusInData[status] =
            reportStatusLookupForTable[status] ?? String(status);
        }
        const reason = row.mr_adjustment_reason_ndx;
        if (reason != null) {
          reasonInData[reason] =
            adjustmentReasonLookupForTable[reason] ?? String(reason);
        }
      });

      return [
        {
          title: "Status",
          field: "_status",
          editable: "never",
          filtering: false,
          sorting: false,
          cellStyle: { width: 70, minWidth: 70, textAlign: "center" },
          headerStyle: { textAlign: "center" },
          render: (rowData) =>
            isUnentered(rowData) ? (
              ""
            ) : (
              <Tooltip title="This reading has been submitted. Contact the District to make a change.">
                <LockIcon fontSize="small" style={{ color: "#888" }} />
              </Tooltip>
            ),
        },
        {
          title: "Month",
          field: "c_month",
          editable: "never",
          lookup: monthsLookupForTable,
          // Redundant when a single month is selected (every row is that month);
          // only needed for the "All" view, where rows span the whole year.
          hidden: selectedMonth !== ALL_MONTHS,
        },
        {
          title: "Meter ID",
          field: "meter_id",
          editable: "never",
          lookup: meterIdLookup,
          cellStyle: { width: 150, minWidth: 150 },
        },
        {
          title: "Last Reading",
          field: "prev_meter_reading",
          editable: "never",
          render: (rowData) => formatter(rowData.prev_meter_reading, 0) ?? "",
          cellStyle: { width: 150, minWidth: 150 },
        },
        {
          title: "New Reading",
          field: "meter_reading",
          editable: (_columnDef, rowData) => isUnentered(rowData),
          render: (rowData) => formatter(rowData.meter_reading, 0) ?? "",
          editComponent: (props) => {
            // A reading below the previous one usually means a meter rollover or
            // a typo. Warn, but don't block — rollovers are legitimate.
            const prev = props.rowData?.prev_meter_reading;
            const value = props.value;
            const looksLow =
              value !== null &&
              value !== "" &&
              value !== undefined &&
              prev !== null &&
              prev !== undefined &&
              Number(value) < Number(prev);
            return (
              <TextField
                value={value ?? ""}
                type="number"
                autoFocus
                onChange={(e) => {
                  props.onChange(e.target.value === "" ? null : e.target.value);
                }}
                InputProps={{
                  inputProps: {
                    onKeyPress: (e) => {
                      if (["e", "."].includes(e.key)) {
                        e.preventDefault();
                      }
                    },
                  },
                }}
                helperText={
                  looksLow
                    ? "Lower than last reading — check for a meter rollover"
                    : undefined
                }
                FormHelperTextProps={{ style: { color: "#b26a00" } }}
              />
            );
          },
          emptyValue: "(empty)",
          // Faint highlight on cells still open for entry, so it's obvious what
          // the owner needs to fill in (shows before and during editing). Locked
          // rows stay neutral.
          cellStyle: (value, rowData) => ({
            width: 120,
            minWidth: 120,
            backgroundColor: isUnentered(rowData) ? "#e3f2fd" : "inherit",
          }),
        },
        {
          title: "Gallons Used",
          field: "calculated_gallons",
          editable: "never",
          render: (rowData) => formatter(rowData.calculated_gallons, 0) ?? "",
          cellStyle: { width: 150, minWidth: 150 },
        },
        {
          title: "Notes",
          field: "notes",
          editable: (_columnDef, rowData) => isUnentered(rowData),
          editComponent: (props) => (
            <TextField
              type="text"
              value={props.value ?? ""}
              onChange={(e) => props.onChange(e.target.value)}
              fullWidth
            />
          ),
          emptyValue: "(empty)",
          cellStyle: { width: 300, minWidth: 300 },
        },
        // Read-only, for transparency: if the District applied an adjustment the
        // owner can see it (and why), but cannot set it here.
        {
          title: "Adjustment",
          field: "adjustment",
          editable: "never",
          render: (rowData) =>
            rowData.adjustment ? formatter(rowData.adjustment, 0) : "—",
          cellStyle: { width: 120, minWidth: 120 },
        },
        {
          title: "Adjustment Reason",
          field: "mr_adjustment_reason_ndx",
          editable: "never",
          lookup: reasonInData,
          render: (rowData) =>
            adjustmentReasonLookupForTable[rowData.mr_adjustment_reason_ndx] ??
            "—",
          cellStyle: { width: 240, minWidth: 240 },
        },
        {
          title: "Reporting Status",
          field: "mr_report_status_ndx",
          editable: "never",
          lookup: reportStatusInData,
          render: (rowData) =>
            reportStatusLookupForTable[rowData.mr_report_status_ndx] ?? "—",
          cellStyle: { width: 160, minWidth: 160 },
        },
      ];
    },
    // Rebuild columns whenever the data changes. material-table mutates the
    // column objects it is handed; reusing a stable reference across data
    // updates inflates their internal state until the page leaks/crashes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      monthsLookupForTable,
      adjustmentReasonLookupForTable,
      reportStatusLookupForTable,
      selectedMonth,
      readings,
    ]
  );

  // Built fresh on every render, on purpose — do NOT hoist to module scope or
  // memoize. material-table MUTATES the column objects it is handed
  // (`columnDef.tableData = {...defaults, ...columnDef.tableData}`), and its
  // width calculation folds each column's previous `tableData.width` back into
  // the next one. Hand it the same objects twice and the width string grows ~8x
  // per rebuild — 24 chars, then 240, then 1,968 ... past 60 MB by the eighth,
  // which is the leak that kills the tab. Fresh objects reset `tableData` and
  // keep it flat.
  const summaryColumns = [
    { title: "Fiscal Year", field: "fiscal_year" },
    { title: "Owner/Permit", field: "grouping_name" },
    {
      title: "Annual Allocation",
      field: "permitted_gallons",
      render: (rowData) => formatter(rowData.permitted_gallons, 0) ?? "",
    },
    {
      title: "YTD Metered",
      field: "metered_gallons",
      render: (rowData) => formatter(rowData.metered_gallons, 0) ?? "",
    },
    {
      title: "% Pumped",
      field: "pct_pumped",
      render: (rowData) =>
        rowData.pct_pumped !== null
          ? (rowData.pct_pumped * 100).toFixed(2)
          : "",
    },
    {
      title: "Over Annual",
      field: "is_overpumped",
      render: (rowData) =>
        rowData.is_overpumped === true
          ? "Yes"
          : rowData.is_overpumped === false
          ? "No"
          : "N/A",
      cellStyle: (value) => ({
        backgroundColor: value ? "orange" : "inherit",
        color: value ? "white" : "inherit",
        fontWeight: value ? 600 : "inherit",
      }),
    },
    {
      title: "Over 80%",
      field: "is_over80pct",
      render: (rowData) =>
        rowData.is_over80pct === true
          ? "Yes"
          : rowData.is_over80pct === false
          ? "No"
          : "N/A",
      cellStyle: (value) => ({
        backgroundColor: value ? "yellow" : "inherit",
        fontWeight: value ? 600 : "inherit",
      }),
    },
  ];

  // Monthly detail columns. Plain config for a plain MUI table — nothing
  // mutates these — but kept in the component alongside summaryColumns so the
  // same rule applies uniformly and nobody hoists them later.
  const monthlyColumns = [
    { id: "mo", label: "Month", align: "left" },
    { id: "pct_base_of_annual", label: "%", align: "right" },
    { id: "baseline", label: "Baseline", align: "right" },
    { id: "drought_stage", label: "Drought Stage", align: "center" },
    { id: "drought_target", label: "Drought Target", align: "right" },
    { id: "pumpage", label: "Pumpage", align: "right" },
    { id: "pct_of_target", label: "% of Target", align: "right" },
    { id: "overage_level", label: "Overage Level", align: "center" },
  ];

  // Genuine "no access" case: the user has no groupings assigned in any year.
  // A per-year empty result can't happen via the selector, since the year list
  // is derived from the years the user actually has groupings.
  const noAssignment = !yearsFetching && years.length === 0;
  const multipleGroupings = groupings.length > 1;

  const selectedGroupingName =
    groupings.find((g) => g.grouping_ndx === selectedGrouping)?.grouping_name ??
    "";
  const monthLabel =
    selectedMonth === ALL_MONTHS
      ? "All months"
      : monthsLookupForTable[selectedMonth];

  return (
    <>
      <Helmet title="Enter Meter Readings" />
      <Typography variant="h3" gutterBottom display="inline">
        Enter Meter Readings
      </Typography>

      <Breadcrumbs aria-label="Breadcrumb" mt={2}>
        <Typography>Enter Meter Readings</Typography>
      </Breadcrumbs>

      <Divider my={6} />

      {yearsFetching ? (
        <Loader />
      ) : noAssignment ? (
        <Alert severity="info">
          <Typography variant="h6" component="div">
            There are no permits assigned to your account. Please contact the
            District if you believe this is an error.
          </Typography>
        </Alert>
      ) : (
        <>
          {selectedGrouping ? (
            <TableContainer
              component={Paper}
              style={{
                marginBottom: "12px",
                border: "solid gray 2px",
                borderRadius: "4px",
              }}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                flexWrap="wrap"
                gridGap={8}
                style={{ padding: "16px" }}
              >
                <Typography
                  variant="h6"
                  component="div"
                  style={{ fontWeight: 600 }}
                >
                  Usage Summary (fiscal year to date)
                </Typography>
                <Tooltip title="Update this summary with the readings you have entered. Takes a few seconds.">
                  {/* span so the tooltip still works while the button is disabled */}
                  <span>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={handleRecalculate}
                      disabled={isRecalculating}
                      startIcon={
                        isRecalculating ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <RefreshIcon />
                        )
                      }
                    >
                      {isRecalculating
                        ? "Recalculating..."
                        : "Recalculate Summary"}
                    </Button>
                  </span>
                </Tooltip>
              </Box>

              {summaryLoading || isRecalculating ? (
                <Box px={4} pb={3}>
                  <Skeleton variant="text" width="35%" height={24} />
                  <Skeleton
                    variant="rect"
                    height={40}
                    style={{ marginTop: 8, borderRadius: 4 }}
                  />
                </Box>
              ) : currentSummary.length > 0 ? (
                <MaterialTable
                  columns={summaryColumns}
                  data={currentSummary}
                  options={{
                    padding: "dense",
                    search: false,
                    showTitle: false,
                    paging: false,
                    toolbar: false,
                  }}
                />
              ) : (
                <Box px={4} pb={3}>
                  <Alert severity="info">
                    No usage summary yet for FY {selectedYear}. Enter a reading,
                    then choose Recalculate Summary.
                  </Alert>
                </Box>
              )}

              <Divider />

              <Typography
                variant="subtitle1"
                component="div"
                style={{ padding: "16px 16px 8px", fontWeight: 600 }}
              >
                Monthly Detail (FY {selectedYear})
              </Typography>

              {monthlyFetching || isRecalculating ? (
                <Box px={4} pb={3}>
                  <Skeleton variant="text" width="35%" height={24} />
                  <Skeleton
                    variant="rect"
                    height={120}
                    style={{ marginTop: 8, borderRadius: 4 }}
                  />
                </Box>
              ) : monthlySummary.length > 0 ? (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {monthlyColumns.map((col) => (
                        <MonthlyHeaderCell key={col.id} align={col.align}>
                          {col.label}
                        </MonthlyHeaderCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthlySummary.map((row) => (
                      <TableRow key={`${row.c_year}-${row.c_month}`}>
                        {monthlyColumns.map((col) => (
                          <MonthlyCell
                            key={col.id}
                            align={col.align}
                            style={getMonthlyCellStyle(row, col.id)}
                          >
                            {renderMonthlyCell(row, col.id)}
                          </MonthlyCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                  {monthlyTotals && (
                    <TableFooter>
                      <TableRow>
                        {monthlyColumns.map((col) => (
                          <MonthlyFooterCell key={col.id} align={col.align}>
                            {renderMonthlyCell(monthlyTotals, col.id)}
                          </MonthlyFooterCell>
                        ))}
                      </TableRow>
                    </TableFooter>
                  )}
                </Table>
              ) : (
                <Box px={4} pb={3}>
                  <Alert severity="info">
                    No monthly detail available for FY {selectedYear} yet.
                    Choose Recalculate Summary to build it from the readings you
                    have entered.
                  </Alert>
                </Box>
              )}
            </TableContainer>
          ) : null}

          <Paper style={{ padding: "10px" }}>
            <Grid container spacing={3}>
              {multipleGroupings && (
                <Grid item xs={12} lg={5}>
                  <FormControl variant="outlined" style={{ width: "100%" }}>
                    <InputLabel id="grouping">Permit Owner/Group</InputLabel>
                    <Select
                      labelId="grouping"
                      label="Permit Owner/Group"
                      value={selectedGrouping ?? ""}
                      onChange={(e) => setSelectedGrouping(e.target.value)}
                    >
                      {groupings.map((option) => (
                        <MenuItem
                          key={option.grouping_ndx}
                          value={option.grouping_ndx}
                        >
                          {option.grouping_name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              <Grid item xs={12} lg={multipleGroupings ? 3 : 4}>
                <FormControl variant="outlined" style={{ width: "100%" }}>
                  <InputLabel id="fiscal-year">Fiscal Year</InputLabel>
                  <Select
                    labelId="fiscal-year"
                    label="Fiscal Year"
                    value={years.includes(selectedYear) ? selectedYear : ""}
                    onChange={(e) => setSelectedYear(e.target.value)}
                  >
                    {years.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} lg={multipleGroupings ? 4 : 4}>
                <FormControl variant="outlined" style={{ width: "100%" }}>
                  {!monthsLookup.length > 0 ? (
                    <Loader />
                  ) : (
                    <>
                      <InputLabel id="month">Month</InputLabel>
                      <Select
                        labelId="month"
                        label="Month"
                        value={selectedMonth}
                        onChange={(e) => setSelectedMonth(e.target.value)}
                      >
                        <MenuItem value={ALL_MONTHS}>All</MenuItem>
                        {monthsLookup.map((option) => (
                          <MenuItem
                            key={option.month_number}
                            value={option.month_number}
                          >
                            {option.month_name}
                          </MenuItem>
                        ))}
                      </Select>
                    </>
                  )}
                </FormControl>
              </Grid>
            </Grid>

            {selectedGrouping ? (
              <TableWrapper>
                <Typography
                  variant="h6"
                  component="div"
                  style={{ padding: "16px", fontWeight: 600 }}
                >
                  Meter Readings — {selectedGroupingName} ({monthLabel}, FY{" "}
                  {selectedYear})
                </Typography>
                <Box px={4} pb={2}>
                  <Alert severity="info">
                    For each month, enter the current total shown on your meter
                    in the <strong>New Reading</strong> column, then save.
                    Gallons used is calculated automatically from your previous
                    reading. If you need to correct a past reading or make an
                    adjustment, please contact the District directly.
                  </Alert>
                </Box>
                <DataAdminTableBulkUpdateMeterReadings
                  key={`${selectedGrouping}-${selectedYear}-${selectedMonth}`}
                  pageSize={60}
                  isLoading={readingsFetching}
                  label="My Meter Readings"
                  columns={columns}
                  data={readings}
                  height="100%"
                  endpoint="my-meter-readings/bulk-update"
                  ndxField="meter_reading_ndx"
                  handleRefresh={refetch}
                  search={false}
                />
              </TableWrapper>
            ) : (
              <Box mt={3}>
                <Alert severity="info">
                  <Typography variant="h6" component="div">
                    Select a permit owner/group above to view and enter meter
                    readings.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Paper>
        </>
      )}
    </>
  );
};

export default MyMeterReadings;
